import { api } from '@core/api/client'

import type { BcfTopic } from './bcfParser'
import {
  type Base,
  type Porta,
  type RespostaDoEnvio,
  type RespostaDoServidor,
  semPendentes,
} from './bcfSincronizar'
import type { Envio } from './bcfSyncProtocolo'

/** A ligação real: HTTP de um lado, IndexedDB do outro.
 *
 *  Fica à parte da sincronização de propósito. O que decide a ordem das
 *  coisas está em `bcfSincronizar.ts` e é testável sem rede; isto é só
 *  o cano, e é a parte que não se consegue testar sem um servidor à
 *  frente.
 */

const BASE = '/api/modules/bcf/sync'

// Base de dados só para a fotografia de acordo. Separada da
// `vn2r-bcf`, que guarda os BCF carregados: acrescentar-lhe uma loja
// obrigava a subir a versão dela, e uma migração falhada de uma base
// que tem o trabalho das pessoas é um risco que não vale a pena correr
// por uma tabela de apoio.
const DB_NOME = 'vn2r-bcf-sync'
const DB_VERSAO = 1
const LOJA = 'base'

let ligacao: Promise<IDBDatabase> | null = null

function abrir(): Promise<IDBDatabase> {
  if (ligacao) return ligacao
  ligacao = new Promise((resolve, reject) => {
    const pedido = indexedDB.open(DB_NOME, DB_VERSAO)
    pedido.onupgradeneeded = () => {
      const db = pedido.result
      if (!db.objectStoreNames.contains(LOJA)) {
        db.createObjectStore(LOJA, { keyPath: 'workspace' })
      }
    }
    pedido.onsuccess = () => resolve(pedido.result)
    pedido.onerror = () => {
      ligacao = null
      reject(pedido.error ?? new Error('Não consegui abrir a base local.'))
    }
  })
  return ligacao
}

async function comLoja<T>(
  modo: IDBTransactionMode,
  fn: (loja: IDBObjectStore) => IDBRequest,
): Promise<T | null> {
  const db = await abrir()
  return new Promise<T | null>((resolve, reject) => {
    const tx = db.transaction(LOJA, modo)
    const pedido = fn(tx.objectStore(LOJA))
    pedido.onsuccess = () => resolve((pedido.result as T) ?? null)
    pedido.onerror = () => reject(pedido.error)
  })
}

/** O registo guardado. A chave (`workspace`, o nome do campo é o da
 *  versão 1 da loja) passou a ser `<workspace>::<projeto>`; as bases
 *  antigas, de quando havia um só BCF por workspace, têm só o
 *  workspace, e podem trazer o projecto a que estavam associadas. */
interface BaseGuardada extends Base {
  workspace: string
  projecto?: string | null
  projectoNome?: string | null
}

/** A chave de uma base: uma por workspace e por BCF. */
export function chaveDaBase(workspace: string, projeto: string): string {
  return `${workspace}::${projeto}`
}

async function lerRegisto(chave: string): Promise<BaseGuardada | null> {
  try {
    return await comLoja<BaseGuardada>('readonly', (l) => l.get(chave))
  } catch {
    return null
  }
}

function semChave(r: BaseGuardada): Base {
  const { workspace: _w, projecto: _p, projectoNome: _n, ...base } = r
  void _w
  void _p
  void _n
  return base
}

/** A base de um BCF, ou null se nunca houve acordo. Sem base guardada
 *  a sincronização ainda funciona: trata tudo como primeira vez e
 *  pergunta mais do que devia. */
export async function lerBaseGuardada(
  workspace: string,
  projeto: string,
): Promise<Base | null> {
  const r = await lerRegisto(chaveDaBase(workspace, projeto))
  return r ? semChave(r) : null
}

/**
 * A base antiga deste workspace, de quando havia um só BCF por
 * workspace, se for deste BCF.
 *
 * É deste BCF se estava associada a ele (beta.169 guardava o projecto),
 * ou se não diz a que BCF pertence (versões anteriores): nesse caso fica
 * para o primeiro BCF que se sincronizar, que era o único.
 */
export async function baseAntigaDoBcf(
  workspace: string,
  projeto: string,
  localId: string,
): Promise<Base | null> {
  const r = await lerRegisto(workspace)
  if (!r) return null
  return baseAntigaPertence(r.projecto ?? null, projeto, localId) ? semChave(r) : null
}

/** A base antiga, associada a `associado`, é deste BCF? Sem associação
 *  (versões anteriores à beta.169) fica para o primeiro que a pedir. A
 *  beta.169 guardava o id local do BCF sem o prefixo `local:`. */
export function baseAntigaPertence(
  associado: string | null,
  projeto: string,
  localId: string,
): boolean {
  return associado === null || associado === projeto || associado === localId
}

export async function gravarBaseGuardada(
  workspace: string,
  projeto: string,
  base: Base,
): Promise<void> {
  try {
    await comLoja('readwrite', (l) => l.put({ ...base, workspace: chaveDaBase(workspace, projeto) }))
  } catch {
    /* sem onde guardar: a próxima sincronização pergunta mais */
  }
}

async function apagarRegisto(chave: string): Promise<void> {
  try {
    await comLoja('readwrite', (l) => l.delete(chave))
  } catch {
    /* já não estava lá */
  }
}

/** Os conflitos que a pessoa acabou de resolver deixam de estar
 *  pendentes, e o tópico volta a ser enviado na sincronização seguinte. */
export async function marcarResolvidos(
  workspace: string,
  projeto: string,
  guid: string,
  campos: readonly string[],
): Promise<void> {
  const base = await lerBaseGuardada(workspace, projeto)
  if (!base) return
  await gravarBaseGuardada(workspace, projeto, semPendentes(base, guid, campos))
}

export async function esquecerBase(workspace: string, projeto: string): Promise<void> {
  await apagarRegisto(chaveDaBase(workspace, projeto))
}

function comProjeto(caminho: string, projeto: string): string {
  const sep = caminho.includes('?') ? '&' : '?'
  return `${caminho}${sep}projeto=${encodeURIComponent(projeto)}`
}

/** A partilha de um BCF sincronizado (ver docs/PARTILHA.md). */
export interface PartilhaDoBcf {
  id: string
  nome: string | null
  pode_gerir: boolean
  workspace_nome: string
}

export const bcfSyncApi = {
  async changes(projeto: string, desde: number): Promise<RespostaDoServidor> {
    return await api.get<RespostaDoServidor>(
      comProjeto(`${BASE}/changes?desde=${encodeURIComponent(String(desde))}`, projeto),
    )
  },

  /** Um lote já partido (ver `emLotes`). `lido` é a posição da leitura:
   *  o servidor recusa os tópicos que mudaram depois dela. `cliente`
   *  identifica a janela no aviso do canal ao vivo, para ela não voltar
   *  a puxar o que acabou de enviar. */
  async push(
    projeto: string,
    envio: Envio,
    lido: number,
    cliente?: string,
  ): Promise<RespostaDoEnvio> {
    return await api.post<RespostaDoEnvio>(`${BASE}/push`, {
      ...envio,
      lido,
      projeto,
      ...(cliente ? { cliente } : {}),
    })
  },

  /** A partilha deste BCF (null se ainda não foi sincronizado). */
  async partilha(projeto: string): Promise<PartilhaDoBcf | null> {
    return await api.get<PartilhaDoBcf | null>(comProjeto(`${BASE}/partilha`, projeto))
  },

  /** Regista o BCF antes da primeira sincronização, com a escolha de
   *  quem o vê (o workspace inteiro ou só quem o partilha). */
  async registarPartilha(
    projeto: string,
    nome: string,
    workspaceInteiro: boolean,
  ): Promise<PartilhaDoBcf> {
    return await api.post<PartilhaDoBcf>(`${BASE}/partilha`, {
      projeto,
      nome,
      workspace_inteiro: workspaceInteiro,
    })
  },

  /** Passa para este BCF o que ficou sem projecto no servidor, de quando
   *  havia um só BCF por workspace. */
  async adoptar(projeto: string): Promise<{ topicos: number; comentarios: number }> {
    return await api.post<{ topicos: number; comentarios: number }>(`${BASE}/adoptar`, { projeto })
  },

  /** Pega no tópico para editar os campos, ou renova o que já se tem. */
  async tomarBloqueio(
    projeto: string,
    topicGuid: string,
    deviceId?: string,
  ): Promise<{ expires_at: string }> {
    return await api.post<{ expires_at: string }>(
      comProjeto(`${BASE}/locks/${encodeURIComponent(topicGuid)}`, projeto),
      { device_id: deviceId ?? null },
    )
  },

  async largarBloqueio(projeto: string, topicGuid: string): Promise<void> {
    await api.delete<void>(comProjeto(`${BASE}/locks/${encodeURIComponent(topicGuid)}`, projeto))
  },
}

/** As operações de I/O da porta que tocam no IndexedDB e no servidor,
 *  à parte para se poderem substituir nos testes. */
export interface Armazem {
  lerBase: (workspace: string, projeto: string) => Promise<Base | null>
  baseAntiga: (workspace: string, projeto: string, localId: string) => Promise<Base | null>
  gravarBase: (workspace: string, projeto: string, base: Base) => Promise<void>
  apagarBaseAntiga: (workspace: string) => Promise<void>
  adoptar: (projeto: string) => Promise<unknown>
}

const ARMAZEM_REAL: Armazem = {
  lerBase: lerBaseGuardada,
  baseAntiga: baseAntigaDoBcf,
  gravarBase: gravarBaseGuardada,
  apagarBaseAntiga: apagarRegisto,
  adoptar: (projeto) => bcfSyncApi.adoptar(projeto),
}

/**
 * Lê a base de um BCF, aproveitando a antiga do workspace se for a dele.
 *
 * A passagem faz-se uma vez: o servidor primeiro passa para este BCF o
 * que ficou sem projecto (`/adoptar`), e só depois a base antiga muda de
 * chave. Se a adopção falhar, nada muda cá e a próxima sincronização
 * tenta outra vez. O estado não se perde: a base antiga é a mesma
 * fotografia, só com outra chave.
 */
export async function lerBaseComPassagem(
  armazem: Armazem,
  workspace: string,
  projeto: string,
  localId: string,
): Promise<Base | null> {
  const actual = await armazem.lerBase(workspace, projeto)
  if (actual) return actual
  const antiga = await armazem.baseAntiga(workspace, projeto, localId)
  if (!antiga) return null
  await armazem.adoptar(projeto)
  await armazem.gravarBase(workspace, projeto, antiga)
  await armazem.apagarBaseAntiga(workspace)
  return antiga
}

/** Monta a porta para um BCF de um workspace, com o estado local que
 *  lhe passarem.
 *
 *  `lerLocal` e `aplicarLocal` vêm de fora porque os tópicos vivem no
 *  store da aplicação, e este ficheiro não tem nada que saber como esse
 *  store está feito. */
export function portaDoBcf(
  workspace: string,
  projeto: string,
  localId: string,
  lerLocal: () => BcfTopic[],
  aplicarLocal: (topicos: BcfTopic[], apagados: string[]) => void,
  armazem: Armazem = ARMAZEM_REAL,
  /** A janela que envia, para o aviso do canal ao vivo. */
  cliente?: string,
): Porta {
  return {
    lerBase: () => lerBaseComPassagem(armazem, workspace, projeto, localId),
    gravarBase: (b) => armazem.gravarBase(workspace, projeto, b),
    lerRemoto: (desde) => bcfSyncApi.changes(projeto, desde),
    enviar: (envio, lido) => bcfSyncApi.push(projeto, envio, lido, cliente),
    lerLocal,
    aplicarLocal,
  }
}
